import React, { useEffect, useMemo, useState } from "react";
import {
  LocationOn as LocationOnIcon,
  QueryBuilder as QueryBuilderIcon,
  VolumeUp as VolumeUpIcon,
  PropaneTank as PropaneTankIcon,
  LocalShipping as LocalShippingIcon,
  LocalOfferOutlined as LocalOfferOutlinedIcon,
} from "@mui/icons-material";

import { EOrderStatusFinally } from "enums/options/orders";
import {
  OrderProps,
  OrderUpdateActions,
} from "store/modules/Orders/types.store";
import { useTranslation } from "react-i18next";
import { format, intervalToDuration, isAfter, addHours } from "date-fns";
import { Box, CircularProgress, Typography } from "@mui/material";
import { formatStreetAddress } from "utils/formatByText";
import { formatOrderDriverLabel } from "pages/ManageOrders/components/CardOrderDetails/OrderDriverSection/getOrderDriverPermissions";

import * as Styled from "./styles";

interface CardAccordionProps {
  data: OrderProps;
  handleOrderUpdate: (id: string, consumerId: string) => void;
  ordersSelected?: number | null;
  indexSelected?: number;
  handleOrderAccept?: (
    type: OrderUpdateActions,
    id: string,
    dealerId: string,
    paymentType: string,
    valeGas: boolean,
  ) => void;
}

const CardAccordion: React.FC<CardAccordionProps> = ({
  data,
  handleOrderUpdate,
  indexSelected,
  ordersSelected,
  handleOrderAccept,
}) => {
  const { t } = useTranslation();

  const [timeRight, setTimeRight] = useState("");
  const [delayValue, setDelayValue] = useState(0);
  const [isDelay, setIsDelayValue] = useState(false);

  const scheduledOrder = useMemo(
    () => Boolean(data.scheduling?.enabled && data.scheduling?.startAt != null),
    [data.scheduling?.enabled, data.scheduling?.startAt],
  );

  const scheduledOrderDate = useMemo(() => {
    return data.scheduling?.startAt != null
      ? new Date(data.scheduling.startAt).getTime()
      : 0;
  }, [data.scheduling?.startAt]);

  useEffect(() => {
    setIsDelayValue(
      isAfter(new Date(data?.createdAt ?? new Date()), new Date()),
    );
  }, [data?.createdAt]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const time = intervalToDuration({
        start: new Date(data?.createdAt ?? Date()),
        end: new Date(Date()),
      });
      if (time) {
        setDelayValue(delayValue + 1);
      }

      setTimeRight(
        format(
          new Date(
            time.years || 0,
            time.months || 0,
            time.days || 0,
            time.hours || 0,
            isDelay ? 0 : time.minutes || 0,
            isDelay ? delayValue : time.seconds,
          ),
          "HH:mm:ss",
        ),
      );
    }, 1000);
    return () => clearInterval(intervalId);
  }, [data?.createdAt, delayValue, isDelay]);

  const cardStatusFinally = (value: string) => {
    switch (value) {
      case EOrderStatusFinally.CONSUMERCANCELLED:
        return "Cliente Cancelou";

      case EOrderStatusFinally.DEALERCANCELLED:
        return "Revenda cancelou";

      case EOrderStatusFinally.DEALERDENIED:
        return "Revenda recusou ";

      case EOrderStatusFinally.TIMEOUTCANCELLED:
        return "Pedido não aceito";

      case EOrderStatusFinally.APPROVERDENIED:
        return "Recusado";

      default:
        return "Finalizado";
    }
  };

  const timeFinalized = data.timeline.find(
    (value) => value.description === data.status,
  )?.occurredAt;

  const driverLabel = formatOrderDriverLabel({
    orderDriver: data?.driver,
    placedOrigin: data?.placedOrigin,
    coverageArea: data?.coverageArea,
  });

  return (
    <Styled.Container
      colorSelected={ordersSelected === indexSelected}
      cadHeight={data.dealerState === "New"}
    >
      <Styled.CardContent
        onClick={() => {
          return handleOrderUpdate(data.id, data.consumer.id);
        }}
      >
        <Styled.ContainerColumn>
          <Styled.Content>
            <Styled.ContentRow>
              <Styled.NameCard variant="body1">
                {data?.consumer?.name}
              </Styled.NameCard>
              <div>
                {data.dealerState === "InProgress" && (
                  <Styled.ContainerStatus colorTheme="primary">
                    <Typography
                      color="rgba(0, 0, 0, 0.87)"
                      variant="caption"
                      fontSize={14}
                    >
                      Em andamento
                    </Typography>
                  </Styled.ContainerStatus>
                )}
                {data.dealerState === "Finalized" && (
                  <Styled.ContainerStatus
                    colorTheme={
                      cardStatusFinally(data.status) !== "Finalizado"
                        ? "secondary"
                        : "primary"
                    }
                  >
                    <Typography
                      color={
                        cardStatusFinally(data.status) !== "Finalizado"
                          ? "#9847F5"
                          : "rgba(0, 0, 0, 0.87)"
                      }
                      variant="caption"
                      fontSize={14}
                    >
                      {cardStatusFinally(data.status)}
                    </Typography>
                  </Styled.ContainerStatus>
                )}

                {timeRight &&
                data.dealerState === "New" &&
                (data.status === "Placed" ||
                  data.status === "DriverAssigned") ? (
                  <Styled.ContainerStatus colorTheme="secondary">
                    <VolumeUpIcon fontSize="small" />
                    <Typography color="#9847F5" variant="caption">
                      {t("pending")} {timeRight}
                    </Typography>
                  </Styled.ContainerStatus>
                ) : (
                  (data.status === "Placed" ||
                    data.status === "DriverAssigned") &&
                  data.dealerState === "New" && (
                    <Box
                      sx={{ display: "flex", fontSize: "5px", width: "50px" }}
                    >
                      <CircularProgress size={25} />
                    </Box>
                  )
                )}
              </div>
            </Styled.ContentRow>

            {scheduledOrder && scheduledOrderDate && (
              <Styled.ScheduledDeliveryOrder>
                <LocalOfferOutlinedIcon fontSize="small" />
                <Typography variant="subtitle2">
                  {` Agendado para hoje entre ${format(
                    new Date(scheduledOrderDate),
                    "HH:mm",
                  )} e ${format(
                    addHours(new Date(scheduledOrderDate), 1),
                    "HH:mm",
                  )}`}
                </Typography>
              </Styled.ScheduledDeliveryOrder>
            )}

            <Styled.NameAdress
              fontSize="14px"
              textAlign="start"
              variant="subtitle2"
            >
              <LocationOnIcon fontSize="medium" />{" "}
              {formatStreetAddress(data?.delivery?.address)}
            </Styled.NameAdress>

            <Styled.NameLocale variant="caption" textAlign="start">
              {data?.delivery?.address?.district}
            </Styled.NameLocale>

            <Styled.ContentRow>
              {data.dealerState !== "Finalized" &&
                !!data?.delivery?.eta?.current && (
                  <Styled.NameAdress variant="subtitle2">
                    <QueryBuilderIcon
                      fontSize="small"
                      sx={{ marginTop: "-2px" }}
                    />
                    Entregar em
                    {` (${data?.delivery?.eta?.current} ${t("minutes")})`}
                  </Styled.NameAdress>
                )}

              {data.dealerState === "Finalized" && !!timeFinalized && (
                <Styled.NameAdress variant="subtitle2">
                  <QueryBuilderIcon
                    fontSize="small"
                    sx={{ marginTop: "-2px" }}
                  />
                  {` Finalizado às ${format(
                    new Date(timeFinalized || ""),
                    "HH:mm",
                  )}`}
                </Styled.NameAdress>
              )}

              <Styled.NameAdress variant="subtitle2">
                <PropaneTankIcon
                  color="primary"
                  fontSize="small"
                  sx={{ marginTop: "-2px" }}
                />
                {data.items.length === 1
                  ? data.items[0].description
                  : "Vários produtos"}
              </Styled.NameAdress>
            </Styled.ContentRow>

            {driverLabel && (
              <Styled.NameAdress variant="subtitle2">
                <LocalShippingIcon fontSize="small" color="inherit" />
                {driverLabel}
              </Styled.NameAdress>
            )}
          </Styled.Content>
        </Styled.ContainerColumn>
      </Styled.CardContent>
      {data.dealerState === "New" && (
        <Styled.ButtonAcceptOrder
          onClick={() =>
            handleOrderAccept &&
            handleOrderAccept(
              "accept",
              data?.id || "",
              data?.dealer?.id || "",
              data?.payment?.type || "",
              data?.voucher?.valeGas || false,
            )
          }
          color="error"
        >
          ACEITAR PEDIDO
        </Styled.ButtonAcceptOrder>
      )}
    </Styled.Container>
  );
};

export default CardAccordion;
